import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  ApplicationInsights,
  ITelemetryItem,
  SeverityLevel,
} from '@microsoft/applicationinsights-web';
import { environment } from '../../../environments/environment';

const CLOUD_ROLE = 'bestie-admin-portal';

@Injectable({ providedIn: 'root' })
export class LogUtilityService {
  private readonly router = inject(Router);
  private readonly appInsights?: ApplicationInsights;
  private userMetaData: Record<string, unknown> | null = null;

  constructor() {
    const instrumentationKey = environment.appInsights.instrumentationKey;

    if (instrumentationKey) {
      this.appInsights = new ApplicationInsights({
        config: { instrumentationKey },
      });
      this.appInsights.loadAppInsights();
      this.appInsights.addTelemetryInitializer((item) => this.applyTelemetryContext(item));

      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.appInsights?.trackPageView({ uri: event.urlAfterRedirects });
        }
      });
    }
  }

  /** Stamps the static app version on the App Insights context. Called once at bootstrap; never throws. */
  initialize(): Promise<void> {
    try {
      const application = this.appInsights?.context?.application;
      if (application) {
        application.ver = environment.version;
      }
    } catch (error) {
      console.error('LogUtilityService.initialize failed', error);
    }
    return Promise.resolve();
  }

  logError(message: string, exception?: unknown): void {
    try {
      if (exception) {
        console.error(message, exception);
        this.appInsights?.trackException({
          exception: exception as Error,
          severityLevel: SeverityLevel.Error,
        });
      } else {
        console.error(message);
        this.appInsights?.trackTrace({ message, severityLevel: SeverityLevel.Error });
      }
    } catch (error) {
      console.error('LogUtilityService.logError failed', error);
    }
  }

  logWarning(message: string, properties?: Record<string, unknown>): void {
    try {
      console.warn(message);
      this.appInsights?.trackTrace({ message, severityLevel: SeverityLevel.Warning }, properties);
    } catch (error) {
      console.error('LogUtilityService.logWarning failed', error);
    }
  }

  logInformation(message: string, properties?: Record<string, unknown>): void {
    try {
      this.appInsights?.trackTrace({ message, severityLevel: SeverityLevel.Information }, properties);
    } catch (error) {
      console.error('LogUtilityService.logInformation failed', error);
    }
  }

  setAuthenticatedUser(userId: Guid, properties?: Record<string, unknown>): void {
    try {
      this.appInsights?.setAuthenticatedUserContext(userId);
      this.userMetaData = properties ?? null;
    } catch (error) {
      console.error('LogUtilityService.setAuthenticatedUser failed', error);
    }
  }

  clearAuthenticatedUser(): void {
    try {
      this.appInsights?.clearAuthenticatedUserContext();
      this.userMetaData = null;
    } catch (error) {
      console.error('LogUtilityService.clearAuthenticatedUser failed', error);
    }
  }

  /** Stamps cloud role + authenticated-user metadata onto every telemetry item. */
  private applyTelemetryContext(item: ITelemetryItem): void {
    item.tags = item.tags ?? {};
    item.tags['ai.cloud.role'] = CLOUD_ROLE;

    if (this.userMetaData) {
      item.data = item.data ?? {};
      for (const [key, value] of Object.entries(this.userMetaData)) {
        item.data[key] = value;
      }
    }
  }
}
